/**
 * The one status vocabulary (P25009-431 BR-5).
 *
 * Every status chip in the product means the same four things:
 *   ok    green — done or running
 *   wait  amber — needs your action
 *   stop  red   — stopped
 *   idle  grey  — not published / inert
 * plus `info` (blue) for scheduled/in-flight states the mockup marks that way.
 *
 * Wheelplace's existing status WORDS are kept (BR-2/BR-5) — this module only
 * decides the colour. Match is exact on the normalised label first, then a few
 * keyword rules for labels that carry a suffix ("Pågår · 67 %", "37 felrader").
 * Unknown labels fall back to grey: a colourless chip is honest, a wrongly
 * green one is not.
 */

export const TONES = ['ok', 'wait', 'stop', 'idle', 'info'];

const EXACT = {
  // green — done or running
  aktiv: 'ok',
  slutförd: 'ok',
  betald: 'ok',
  utbetald: 'ok',
  levererad: 'ok',
  publicerad: 'ok',
  godkänd: 'ok',
  klar: 'ok',
  inne: 'ok',
  tillgänglig: 'ok',
  'visas publikt': 'ok',
  skickad: 'ok',
  bekräftad: 'ok',
  pågår: 'ok',
  körs: 'ok', // bulk import running
  konverterad: 'ok', // storage record turned into a listing
  accepterad: 'ok', // sub-user invitation
  'på väg': 'ok', // payout in transit — running, not waiting on the seller
  annons: 'ok', // a live ad (P25009-410 — as opposed to a catalogue row)

  // amber — needs your action
  'snart slut': 'wait',
  inbjuden: 'wait',
  granska: 'wait',
  'väntar på leverans': 'wait',
  'väntar på betalning': 'wait',
  reserverad: 'wait',
  'slutförd med fel': 'wait', // the import finished but its error rows need the seller
  'inbyte · dold': 'wait', // a hidden trade-in, waiting to be made a sale ad (P25009-485)
  invändning: 'wait', // receipt objection — needs a response

  // red — stopped
  avbruten: 'stop',
  avbröts: 'stop',
  misslyckad: 'stop',
  misslyckades: 'stop',
  nekad: 'stop',
  stoppad: 'stop',
  'nyckel utgången': 'stop',
  försenad: 'stop',
  uppsagd: 'stop', // storage agreement terminated

  // grey — not published / inert
  utkast: 'idle',
  dold: 'idle',
  pausad: 'idle',
  stängd: 'idle',
  arkiverad: 'idle',
  utlämnad: 'idle',
  såld: 'idle',
  inaktiv: 'idle',
  återlämnad: 'idle', // storage wheels handed back
  borttagen: 'idle',
  okänd: 'idle',
  // P25009-410 — on the seller's profile, deliberately not on the marketplace.
  katalog: 'idle',
  återställd: 'idle', // an import the seller undid

  // blue — scheduled / informational
  planerad: 'info',
  schemalagd: 'info',
  skapad: 'info', // payout created but not yet moving
  boostad: 'info',
};

const KEYWORD_RULES = [
  { test: /^väntar/, tone: 'wait' },
  { test: /fel/, tone: 'stop' },
  { test: /avbr/, tone: 'stop' },
  { test: /utgången|utgått/, tone: 'stop' },
  { test: /^pågår/, tone: 'ok' },
  { test: /kvar$/, tone: 'wait' }, // "1 kvar", "2 dagar kvar"
];

/**
 * Colour tone for a status label. Accepts the label as rendered ("Pågår · 67 %")
 * and is case-insensitive. Unknown → 'idle'.
 */
export const toneForStatus = label => {
  if (!label || typeof label !== 'string') return 'idle';
  const normalised = label.trim().toLowerCase();

  if (EXACT[normalised]) return EXACT[normalised];

  // "Pågår · 67 %" → try the part before the separator too.
  const head = normalised.split('·')[0].trim();
  if (EXACT[head]) return EXACT[head];

  const rule = KEYWORD_RULES.find(r => r.test.test(normalised));
  return rule ? rule.tone : 'idle';
};
